import { ICategory, ICategoryGroup, ICategoryGroupTree } from "@defs/category";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { ICategoryTag } from "@defs/category-tag";
import { PayloadActionPendingMeta } from "@defs/types";

interface CategoriesState {
  tree: {
    data: ICategoryGroupTree[];
    relationship: boolean;
    loading: boolean;
  };

  groups: {
    data: ICategoryGroup[];
    loading: boolean;
  };

  list: {
    loading: boolean;
    data: ICategoryGroup[];
  };

  detail: {
    updating: boolean;
    loading: boolean;
    data: ICategory;
    error: boolean;
  };

  tags: {
    category: string[];
    loading: boolean;
    data: ICategoryTag[];
  };
}

const initialState: CategoriesState = {
  tree: {
    data: [],
    relationship: false,
    loading: false,
  },

  groups: {
    data: [],
    loading: false,
  },

  list: {
    loading: false,
    data: [],
  },

  detail: {
    updating: false,
    loading: false,
    data: null,
    error: false,
  },

  tags: {
    category: undefined,
    loading: false,
    data: [],
  },
};

const categoriesSlice = createSlice({
  name: "categories",
  initialState,
  reducers: {
    resetDetail(state) {
      state.detail.loading = true;
      state.detail.data = initialState.detail.data;
    },
  },
  extraReducers: {
    "categories/tree/pending": (
      state,
      action: PayloadAction<undefined, any, PayloadActionPendingMeta<boolean>>
    ) => {
      state.tree.loading = true;
      state.tree.relationship = action.meta.arg;
    },

    "categories/tree/fulfilled": (
      state,
      action: PayloadAction<ICategoryGroupTree[]>
    ) => {
      state.tree.data = action.payload;
      state.tree.loading = false;
    },

    "categories/list/pending": (state) => {
      state.list.loading = true;
      state.list.data = initialState.list.data;
    },

    "categories/list/fulfilled": (
      state,
      action: PayloadAction<ICategoryGroup[]>
    ) => {
      state.list.loading = false;
      state.list.data = action.payload;
    },

    "category/details/pending": (state) => {
      state.detail.loading = true;
      state.detail.data = initialState.detail.data;
    },

    "category/details/fulfilled": (state, action: PayloadAction<ICategory>) => {
      state.detail.loading = false;
      state.detail.data = action.payload;
    },

    "categories/groups/pending": (state) => {
      state.groups.loading = true;
      state.groups.data = initialState.groups.data;
    },

    "categories/groups/fulfilled": (
      state,
      action: PayloadAction<ICategory[]>
    ) => {
      state.groups.loading = false;
      state.groups.data = action.payload;
    },

    "category/update/pending": (state) => {
      state.detail.updating = true;
    },

    "category/update/fulfilled": (state, action: PayloadAction<ICategory>) => {
      state.detail.updating = false;
      state.detail.data = action.payload;
    },

    "category/update/rejected": (state) => {
      state.detail.updating = false;
      state.detail.error = true;
    },

    "category/create/pending": (state) => {
      state.detail.updating = true;
    },

    "category/create/fulfilled": (state) => {
      state.detail.updating = false;
    },

    "category/create/rejected": (state) => {
      state.detail.updating = false;
      state.detail.error = true;
    },

    // TAGS
    "category/tags/pending": (
      state,
      action: PayloadAction<undefined, any, PayloadActionPendingMeta<string[]>>
    ) => {
      state.tags.loading = true;
      state.tags.category = action.meta.arg;
    },

    "category/tags/fulfilled": (
      state,
      action: PayloadAction<ICategoryTag[]>
    ) => {
      state.tags.loading = false;
      state.tags.data = action.payload;
    },
  },
});

export const categoriesActions = categoriesSlice.actions;

export default categoriesSlice.reducer;
